import { NumberHelper } from 'core/helpers';
import { PricingHelper } from 'pricing/helpers';
import { IPricingProfilePartPricing } from 'pricing/types';

type TNumericValue = string | number | null | undefined;

export const MARGIN_DECIMALS = 2;

export const toNumericValue = (value: TNumericValue): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) ? parsedValue : null;
};

// validate-part keys `pricing` by UOM and the snake -> camel mapper leaves fully uppercase keys
// untouched, but a mixed-case UOM ("Each") would arrive camel-cased — match case-insensitively.
export const getUomPricing = (
  pricing?: Record<string, IPricingProfilePartPricing>,
  uom?: string
): IPricingProfilePartPricing | null => {
  if (!pricing || !uom) {
    return null;
  }

  if (pricing[uom]) {
    return pricing[uom];
  }

  const matchingUom = Object.keys(pricing).find((key) => key.toLowerCase() === uom.toLowerCase());

  return matchingUom ? pricing[matchingUom] : null;
};

export const getCostSpread = (purchaseCost: TNumericValue, standardCost: TNumericValue) =>
  PricingHelper.computeGpm(toNumericValue(purchaseCost), toNumericValue(standardCost));

export const deriveStandardCost = (
  purchaseCost: TNumericValue,
  standardCostMultiplier: TNumericValue,
  decimals: number
): number | null => {
  const parsedPurchaseCost = toNumericValue(purchaseCost);
  const multiplier = toNumericValue(standardCostMultiplier);

  if (!(parsedPurchaseCost > 0) || multiplier === null || multiplier >= 1) {
    return null;
  }

  return NumberHelper.round(parsedPurchaseCost / (1 - multiplier), decimals);
};

export const derivePurchaseCost = (
  standardCost: TNumericValue,
  standardCostMultiplier: TNumericValue,
  decimals: number
): number | null => {
  const parsedStandardCost = toNumericValue(standardCost);
  const multiplier = toNumericValue(standardCostMultiplier);

  if (!(parsedStandardCost > 0) || multiplier === null || multiplier >= 1) {
    return null;
  }

  return NumberHelper.round(parsedStandardCost * (1 - multiplier), decimals);
};

// The cost the fixed price/margin pair is derived from: the entered standard cost when set,
// otherwise the company standard cost for the selected UOM.
export const getCostBasis = (standardCost: TNumericValue, companyStandardCost: TNumericValue) => {
  const parsedStandardCost = toNumericValue(standardCost);

  if (parsedStandardCost > 0) {
    return parsedStandardCost;
  }

  return toNumericValue(companyStandardCost);
};

export const deriveFixedPrice = (
  costBasis: TNumericValue,
  marginPercentage: TNumericValue,
  decimals: number
): number | null => {
  const parsedCostBasis = toNumericValue(costBasis);
  const parsedMargin = toNumericValue(marginPercentage);

  if (!(parsedCostBasis > 0) || !(parsedMargin > 0)) {
    return null;
  }

  const price = PricingHelper.computePrice(parsedCostBasis, parsedMargin / 100);

  return price === null ? null : NumberHelper.round(Number(price), decimals);
};

export const deriveMarginPercentage = (
  costBasis: TNumericValue,
  price: TNumericValue,
  decimals: number
): number | null => {
  const parsedCostBasis = toNumericValue(costBasis);
  const parsedPrice = toNumericValue(price);

  if (!(parsedCostBasis > 0) || !(parsedPrice > 0)) {
    return null;
  }

  return PricingHelper.computeGpmPercentage(parsedCostBasis, parsedPrice, decimals);
};
